import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth, API } from "@/App";
import { toast } from "sonner";

const EKIN_LOGO = "https://customer-assets.emergentagent.com/job_delivery-status-30/artifacts/fgciba1d_ekin_logo_HR.png";

function formatErr(detail) {
  if (!detail) return "Error";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(", ");
  return detail?.msg || String(detail);
}

export default function Login() {
  const { user, loading, refetch } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/login`, { email: email.trim().toLowerCase(), password });
      await refetch();
      toast.success("Sesión iniciada");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Error al iniciar sesión");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-[1fr_1.1fr] bg-white" data-testid="login-page">
      {/* ===== LEFT: Form column ===== */}
      <div className="relative flex flex-col justify-between p-8 sm:p-12 lg:p-16">
        {/* Top: logo */}
        <div>
          <img
            src={EKIN_LOGO}
            alt="EKIN"
            className="h-14 sm:h-16 w-auto object-contain"
            data-testid="ekin-logo"
            style={{ filter: "drop-shadow(0 1px 0 rgba(26,58,140,0.05))" }}
          />
        </div>

        {/* Middle: form */}
        <div className="max-w-md w-full mx-auto sm:mx-0 my-12 sm:my-0" data-testid="login-card">
          <p
            className="eyebrow mb-4"
            style={{ color: "#1A3A8C", letterSpacing: "0.28em" }}
            data-testid="eyebrow-thinking-together"
          >
            Thinking together · Desde 1963
          </p>

          <h1 className="font-display font-bold leading-[0.95] mb-5 tracking-tight"
              style={{ fontSize: "clamp(2.25rem, 4vw, 3.25rem)", color: "#0B1B3E" }}>
            Sistema de entregas
            <span className="block" style={{ color: "#1A3A8C" }}>EKIN.</span>
          </h1>

          <p className="text-base leading-relaxed mb-10" style={{ color: "#475569" }}>
            Coordinación de embarques de brochas, cremalleras y máquinas — de
            fábrica a cliente, paso a paso.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" autoComplete="on">
            <div>
              <label
                htmlFor="login-email"
                className="eyebrow mb-2 block"
                style={{ color: "#1A3A8C", letterSpacing: "0.2em" }}
              >
                Email
              </label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@ekin.com"
                data-testid="input-email"
                autoComplete="email"
                className="w-full px-4 py-4 border-2 bg-white text-base focus:outline-none transition-colors"
                style={{ borderColor: "#E2E8F0", color: "#0B1B3E" }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#1A3A8C")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#E2E8F0")}
              />
            </div>

            <div>
              <label
                htmlFor="login-password"
                className="eyebrow mb-2 block"
                style={{ color: "#1A3A8C", letterSpacing: "0.2em" }}
              >
                Contraseña
              </label>
              <input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="input-password"
                autoComplete="current-password"
                className="w-full px-4 py-4 border-2 bg-white text-base focus:outline-none transition-colors"
                style={{ borderColor: "#E2E8F0", color: "#0B1B3E" }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#1A3A8C")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#E2E8F0")}
              />
            </div>

            <button
              type="submit"
              disabled={!email.trim() || !password || submitting}
              data-testid="btn-login"
              className="btn-tactile w-full mt-2 flex items-center justify-center gap-3 px-6 py-4 font-medium transition-all disabled:opacity-50 group"
              style={{ backgroundColor: "#1A3A8C", color: "#fff" }}
              onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = "#122B6B")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1A3A8C")}
            >
              {submitting ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <span className="tracking-wide">Iniciar sesión</span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          <p className="mt-10 text-xs" style={{ color: "#94A3B8" }}>
            ¿No tienes acceso? Solicítalo al administrador del sistema.
          </p>
        </div>

        {/* Bottom: footer */}
        <div className="pt-8 border-t" style={{ borderColor: "#E2E8F0" }}>
          <div className="grid grid-cols-3 gap-6 text-[11px] uppercase tracking-[0.18em]" style={{ color: "#475569" }}>
            <div>
              <p className="font-bold mb-1" style={{ color: "#1A3A8C" }}>Automoción</p>
              <p>Componentes</p>
            </div>
            <div>
              <p className="font-bold mb-1" style={{ color: "#1A3A8C" }}>Aeronáutica</p>
              <p>Alta precisión</p>
            </div>
            <div>
              <p className="font-bold mb-1" style={{ color: "#1A3A8C" }}>Energía</p>
              <p>Gran formato</p>
            </div>
          </div>
        </div>
      </div>

      {/* ===== RIGHT: Hero panel ===== */}
      <div
        className="hidden lg:block relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0B1B3E 0%, #1A3A8C 55%, #2D55B5 100%)" }}
        data-testid="hero-panel"
      >
        {/* Diagonal chevrons motif (echoes Ekin logo) */}
        <svg
          className="absolute -left-20 top-1/2 -translate-y-1/2 opacity-[0.07]"
          width="900"
          height="900"
          viewBox="0 0 900 900"
          fill="none"
          aria-hidden="true"
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <path
              key={i}
              d={`M ${100 + i * 110} 100 L ${340 + i * 110} 450 L ${100 + i * 110} 800`}
              stroke="#FFFFFF"
              strokeWidth="22"
              strokeLinecap="square"
              fill="none"
            />
          ))}
        </svg>

        {/* Background image: industrial machining */}
        <img
          src="https://images.unsplash.com/photo-1565043589221-1a6fd9ae45c7?auto=format&fit=crop&w=1600&q=80"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-luminosity"
        />

        {/* Soft top fade */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,27,62,0.85) 0%, rgba(11,27,62,0.55) 50%, rgba(45,85,181,0.55) 100%)" }} />

        {/* Content */}
        <div className="relative h-full flex flex-col justify-between p-12 lg:p-16 text-white">
          {/* Top eyebrow */}
          <div>
            <p className="eyebrow text-white/60 mb-2" style={{ letterSpacing: "0.3em" }}>
              Your Global Broaching · Rolling Partner
            </p>
          </div>

          {/* Center quote */}
          <div className="max-w-xl">
            <p className="font-display font-bold leading-[1.05] mb-6"
               style={{ fontSize: "clamp(2.5rem, 4.2vw, 3.75rem)" }}>
              Soluciones integrales,
              <br />
              <span className="text-white/90">entregadas con precisión.</span>
            </p>
            <p className="text-base sm:text-lg text-white/75 max-w-md leading-relaxed">
              Cada brocha, cada máquina y cada componente sigue el mismo
              camino: factura, embarque, ruta, entrega — controlado por el equipo Ekin.
            </p>
          </div>

          {/* Bottom: stages */}
          <div className="grid grid-cols-4 gap-3" data-testid="hero-stages">
            {[
              { n: "01", t: "Factura" },
              { n: "02", t: "Embarque" },
              { n: "03", t: "En ruta" },
              { n: "04", t: "Entregado" },
            ].map((s) => (
              <div key={s.n} className="border-l-2 border-white/40 pl-3 py-1">
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1">
                  {s.n}
                </p>
                <p className="font-display text-base font-bold">{s.t}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
